import { type Line, type Segment } from "./base";
import { normalizedWidth } from "./base";

export function pad(s: string, w: number, right?: boolean): string {
  if (s.length >= w) return s.slice(0, w);
  const space = " ".repeat(w - s.length);
  return right ? space + s : s + space;
}

export function hl(n: number): string {
  return "\u2500".repeat(normalizedWidth(n));
}

export function bar(val: number, max: number, w: number): string {
  const width = normalizedWidth(w);
  if (width <= 0) return "";
  if (!Number.isFinite(max) || max <= 0) return "\u2591".repeat(width);

  const ratio = Number.isFinite(val) ? val / max : 0;
  const filled = Math.round(Math.min(1, Math.max(0, ratio)) * width);
  return "\u2588".repeat(filled) + "\u2591".repeat(width - filled);
}

export function spark(data: number[], w: number): string {
  const width = normalizedWidth(w);
  if (width <= 0 || data.length === 0) return "";

  const ticks = "\u2581\u2582\u2583\u2584\u2585\u2586\u2587\u2588";
  const v = data.slice(-width);
  const mn = Math.min(...v);
  const mx = Math.max(...v);
  const rg = mx - mn || 1;
  return v.map((x) => ticks[Math.round(((x - mn) / rg) * 7)]).join("");
}

function segLen(seg: Segment): number {
  return typeof seg === "string" ? seg.length : seg.text.length;
}

function lineLen(line: Line): number {
  let n = 0;
  for (const seg of line) n += segLen(seg);
  return n;
}

function sliceSeg(seg: Segment, end: number): Segment | null {
  if (end <= 0) return null;
  if (typeof seg === "string") {
    const text = seg.slice(0, end);
    return text.length > 0 ? text : null;
  }
  const text = seg.text.slice(0, end);
  if (text.length === 0) return null;
  return { ...seg, text };
}

function truncateLine(line: Line, w: number): Line {
  if (w <= 0) return [];
  const out: Line = [];
  let remaining = w;

  for (const seg of line) {
    if (remaining <= 0) break;
    const len = segLen(seg);
    if (len <= remaining) {
      out.push(seg);
      remaining -= len;
      continue;
    }
    const clipped = sliceSeg(seg, remaining);
    if (clipped) out.push(clipped);
    remaining = 0;
  }

  return out;
}

export function padLine(line: Line, w: number): Line {
  if (w <= 0) return [];
  const used = lineLen(line);
  if (used > w) return truncateLine(line, w);
  if (used === w) return line;
  return [...line, { text: " ".repeat(w - used), noSelect: true }];
}

export function truncate(text: string, width: number, ellipsis = "\u2026"): string {
  if (text.length <= width) return text;
  if (width <= ellipsis.length) return ellipsis.slice(0, width);
  return text.slice(0, width - ellipsis.length) + ellipsis;
}

export function wrapText(text: string, w: number): string[] {
  if (w <= 0) return [""];
  if (text.length <= w) return [text];

  const result: string[] = [];
  let pos = 0;
  while (pos < text.length) {
    if (pos + w >= text.length) {
      result.push(text.slice(pos));
      break;
    }
    let breakAt = text.lastIndexOf(" ", pos + w);
    if (breakAt <= pos) {
      // No word boundary — hard break
      result.push(text.slice(pos, pos + w));
      pos += w;
    } else {
      result.push(text.slice(pos, breakAt));
      pos = breakAt + 1;
    }
  }
  return result.length > 0 ? result : [""];
}
